import { useCallback, useEffect, useState } from "react";
import { Target, Save, Gift, Users, DollarSign, Percent } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { listMetasVendedores, upsertMetaVendedor } from "@/lib/api";
import { fmtBRL, fmtInt, fmtPct, pct } from "@/lib/format";

/**
 * Admin — matriz de metas por vendedor (2 dimensões + bônus individual):
 * - Meta financeira (R$/mês) — receita
 * - Meta em clientes (qtd/mês) — nº de contratos fechados
 * - Bônus individual (%) — sobrescreve o bônus global; vazio = herda config.
 */
export default function MetasVendedor({ metrics = [], bonusPct = 0 }) {
  // { [vendedor]: { meta_mensal, meta_clientes, bonus_pct|null } }
  const [metas, setMetas] = useState({});
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    try {
      const list = await listMetasVendedores();
      const map = {};
      for (const m of list) {
        map[m.vendedor] = {
          meta_mensal: Number(m.meta_mensal || 0),
          meta_clientes: Number(m.meta_clientes || 0),
          bonus_pct: m.bonus_pct === null || m.bonus_pct === undefined ? null : Number(m.bonus_pct),
        };
      }
      setMetas(map);
    } catch (err) {
      console.error("Erro ao carregar metas de vendedores:", err);
      toast.error("Não foi possível carregar as metas");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setDraft = (vendedor, key, value) =>
    setDrafts((prev) => ({ ...prev, [vendedor]: { ...(prev[vendedor] || {}), [key]: value } }));

  const handleSave = async (vendedor) => {
    const cur = metas[vendedor] || { meta_mensal: 0, meta_clientes: 0, bonus_pct: null };
    const d = drafts[vendedor] || {};
    const patch = {
      meta_mensal: Number(d.meta_mensal !== undefined ? d.meta_mensal : cur.meta_mensal) || 0,
      meta_clientes: Number(d.meta_clientes !== undefined ? d.meta_clientes : cur.meta_clientes) || 0,
    };
    // Bônus individual: string vazia = reset pra herdar global (-1 no backend).
    // Valor numérico: interpretado como % (input UX) e convertido pra fração 0..1.
    if (d.bonus_pct_pct !== undefined) {
      const raw = String(d.bonus_pct_pct).trim();
      patch.bonus_pct = raw === "" ? -1 : Math.max(0, Number(raw) || 0) / 100;
    }
    setSaving(vendedor);
    try {
      const saved = await upsertMetaVendedor(vendedor, patch);
      setMetas((prev) => ({
        ...prev,
        [vendedor]: {
          meta_mensal: Number(saved.meta_mensal || 0),
          meta_clientes: Number(saved.meta_clientes || 0),
          bonus_pct: saved.bonus_pct === null || saved.bonus_pct === undefined ? null : Number(saved.bonus_pct),
        },
      }));
      setDrafts((prev) => { const n = { ...prev }; delete n[vendedor]; return n; });
      toast.success(`Metas de ${vendedor} salvas`);
    } catch { toast.error("Erro ao salvar meta"); }
    finally { setSaving(null); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="metas-vendedor">
      <div className="flex items-center gap-2 mb-4">
        <Target className="w-4 h-4 text-primary" />
        <h3 className="font-heading font-semibold text-lg">Metas por vendedor</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Três configurações por vendedor: <strong>Meta financeira</strong>, <strong>Meta em clientes</strong> e{" "}
        <strong>Bônus individual</strong>. Bônus vazio herda o global{" "}
        (<span className="font-semibold text-amber-500">{(bonusPct * 100).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%</span>);
        aplicado sobre a comissão base ao bater a meta financeira.
      </p>

      {metrics.length === 0 && <div className="text-sm text-muted-foreground">Sem vendedores cadastrados.</div>}

      <div className="space-y-4">
        {metrics.map((v) => {
          const cur = metas[v.nome] || { meta_mensal: 0, meta_clientes: 0, bonus_pct: null };
          const d = drafts[v.nome] || {};
          const metaFin = Number(d.meta_mensal !== undefined ? d.meta_mensal : cur.meta_mensal) || 0;
          const metaCli = Number(d.meta_clientes !== undefined ? d.meta_clientes : cur.meta_clientes) || 0;
          // Input UX: exibe em %, backend guarda fração.
          const bonusDraftRaw = d.bonus_pct_pct;
          const bonusInputValue =
            bonusDraftRaw !== undefined
              ? bonusDraftRaw
              : cur.bonus_pct === null || cur.bonus_pct === undefined
                ? ""
                : (Number(cur.bonus_pct) * 100).toString();
          const bonusEffective = cur.bonus_pct === null || cur.bonus_pct === undefined
            ? bonusPct
            : Number(cur.bonus_pct);
          const isCustomBonus = cur.bonus_pct !== null && cur.bonus_pct !== undefined;
          const pFin = pct(v.receita, metaFin);
          const pCli = pct(v.vendas, metaCli);
          const toneFin = pFin >= 100 ? "text-emerald-500" : pFin >= 50 ? "text-amber-500" : "text-red-500";
          const toneCli = pCli >= 100 ? "text-emerald-500" : pCli >= 50 ? "text-amber-500" : "text-red-500";
          return (
            <div key={v.nome} className="rounded-lg border border-border p-4" data-testid={`meta-vendedor-${v.nome}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div className="min-w-0 font-heading font-semibold truncate flex items-center gap-2">
                  {v.nome}
                  {pFin >= 100 && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-500 border border-amber-500/40">
                      <Gift className="w-3 h-3" /> Bônus aplicado
                    </span>
                  )}
                  {isCustomBonus && (
                    <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary border-primary/40">
                      Bônus custom {(Number(cur.bonus_pct) * 100).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%
                    </Badge>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Financeira */}
                <div className="rounded-md border border-border/70 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">
                      <DollarSign className="w-3 h-3 text-emerald-500" strokeWidth={2} /> Financeira
                    </div>
                    <div className={`font-heading text-lg font-extrabold tabular-nums ${toneFin}`}>{fmtPct(pFin)}</div>
                  </div>
                  <Progress value={Math.min(pFin, 100)} className="h-2 mb-2" />
                  <div className="text-[11px] text-muted-foreground mb-2 tabular-nums">
                    <span className="text-foreground font-mono">{fmtBRL(v.receita)}</span> de <span className="text-foreground font-mono">{fmtBRL(metaFin)}</span>
                  </div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-widest">Meta (R$)</label>
                  <Input type="number" min="0" step="0.01"
                    value={d.meta_mensal !== undefined ? d.meta_mensal : cur.meta_mensal}
                    onChange={(e) => setDraft(v.nome, "meta_mensal", e.target.value)}
                    data-testid={`input-meta-fin-${v.nome}`}
                  />
                </div>

                {/* Clientes */}
                <div className="rounded-md border border-border/70 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">
                      <Users className="w-3 h-3 text-primary" strokeWidth={2} /> Clientes
                    </div>
                    <div className={`font-heading text-lg font-extrabold tabular-nums ${toneCli}`}>{fmtPct(pCli)}</div>
                  </div>
                  <Progress value={Math.min(pCli, 100)} className="h-2 mb-2" />
                  <div className="text-[11px] text-muted-foreground mb-2 tabular-nums">
                    <span className="text-foreground font-mono">{fmtInt(v.vendas)}</span> de <span className="text-foreground font-mono">{fmtInt(metaCli)}</span> clientes
                  </div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-widest">Meta (qtd)</label>
                  <Input type="number" min="0" step="1"
                    value={d.meta_clientes !== undefined ? d.meta_clientes : cur.meta_clientes}
                    onChange={(e) => setDraft(v.nome, "meta_clientes", e.target.value)}
                    data-testid={`input-meta-cli-${v.nome}`}
                  />
                </div>

                {/* Bônus individual */}
                <div className="rounded-md border border-border/70 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">
                      <Percent className="w-3 h-3 text-amber-500" strokeWidth={2} /> Bônus individual
                    </div>
                    <div className="font-heading text-lg font-extrabold tabular-nums text-amber-500">
                      {(Number(bonusEffective) * 100).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%
                    </div>
                  </div>
                  <div className="text-[11px] text-muted-foreground mb-2">
                    Aplicado sobre a comissão base quando bater a meta financeira.
                  </div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-widest">
                    Bônus (%) — vazio herda global
                  </label>
                  <Input type="number" min="0" step="0.1" placeholder={`${(bonusPct * 100).toFixed(1)}% (global)`}
                    value={bonusInputValue}
                    onChange={(e) => setDraft(v.nome, "bonus_pct_pct", e.target.value)}
                    data-testid={`input-meta-bonus-${v.nome}`}
                  />
                </div>
              </div>

              <div className="mt-3 flex justify-end">
                <Button onClick={() => handleSave(v.nome)} disabled={saving === v.nome} data-testid={`btn-save-meta-${v.nome}`}>
                  <Save className="w-4 h-4 mr-2" /> Salvar
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
